import {
  isProduction,
  BOAT_TRADER_SOURCE_ID,
  YACHTWORLD_SOURCE_ID,
  BOATSDOTCOM_SOURCE_ID,
  BOATTRADER_ORIGIN,
  BOATTRADER_QA_ORIGIN,
  BOATTRADER_BOAT_LOANS_URL_QA,
  BOATTRADER_BOAT_LOANS_URL_PROD,
  BOATTRADER_COMBINED_FUNNEL_URL_QA,
  BOATTRADER_COMBINED_FUNNEL_URL_PROD,
} from '../constants';

export interface PortalBranding {
  label: string;
  rootUrl: string;
  boatLoansUrl: string;
  combinedFunnelUrl: string;
  applyPathPrefix: string;
}

interface PortalBrandingByEnv {
  qa: PortalBranding;
  production: PortalBranding;
}

const BOAT_TRADER_BRANDING: PortalBrandingByEnv = {
  qa: {
    label: 'Boat Trader',
    rootUrl: `${BOATTRADER_QA_ORIGIN}/`,
    boatLoansUrl: BOATTRADER_BOAT_LOANS_URL_QA,
    combinedFunnelUrl: BOATTRADER_COMBINED_FUNNEL_URL_QA,
    applyPathPrefix: '/boat-loans/apply',
  },
  production: {
    label: 'Boat Trader',
    rootUrl: `${BOATTRADER_ORIGIN}/`,
    boatLoansUrl: BOATTRADER_BOAT_LOANS_URL_PROD,
    combinedFunnelUrl: BOATTRADER_COMBINED_FUNNEL_URL_PROD,
    applyPathPrefix: '/boat-loans/apply',
  },
};

const YACHTWORLD_BRANDING: PortalBrandingByEnv = {
  qa: {
    label: 'YachtWorld',
    rootUrl: 'https://qa.yachtworld.com/',
    boatLoansUrl: 'https://qa.yachtworld.com/yacht-loans/',
    combinedFunnelUrl:
      'https://qa.yachtworld.com/yacht-loans/apply/loan-application/combined/1044/OFEW5N3V_468/?source=101442&purpose=Boat&prodTesting=true',
    applyPathPrefix: '/yacht-loans/apply',
  },
  production: {
    label: 'YachtWorld',
    rootUrl: 'https://www.yachtworld.com/',
    boatLoansUrl: 'https://www.yachtworld.com/yacht-loans/',
    combinedFunnelUrl:
      'https://www.yachtworld.com/yacht-loans/apply/loan-application/combined/1044/OFEW5N3V_468/?source=101442&purpose=Boat',
    applyPathPrefix: '/yacht-loans/apply',
  },
};

const BOATSDOTCOM_BRANDING: PortalBrandingByEnv = {
  qa: {
    label: 'Boats.com',
    rootUrl: 'https://www.qa.boats.com/',
    boatLoansUrl: 'https://www.qa.boats.com/boat-loans/',
    combinedFunnelUrl:
      'https://www.qa.boats.com/boat-loans/apply/loan-application/combined/1044/OFEW5N3V_468/?source=101460&purpose=Boat&prodTesting=true',
    applyPathPrefix: '/boat-loans/apply',
  },
  production: {
    label: 'Boats.com',
    rootUrl: 'https://www.boats.com/',
    boatLoansUrl: 'https://www.boats.com/boat-loans/',
    combinedFunnelUrl:
      'https://www.boats.com/boat-loans/apply/loan-application/combined/1044/OFEW5N3V_468/?source=101460&purpose=Boat',
    applyPathPrefix: '/boat-loans/apply',
  },
};

const PORTAL_BRANDING_BY_SOURCE_ID: Record<number, PortalBrandingByEnv> = {
  [BOAT_TRADER_SOURCE_ID]: BOAT_TRADER_BRANDING,
  [YACHTWORLD_SOURCE_ID]: YACHTWORLD_BRANDING,
  [BOATSDOTCOM_SOURCE_ID]: BOATSDOTCOM_BRANDING,
};

// Every hostname each portal is actually served from (QA and production), used to
// infer a source id when a `source` query param is missing -- e.g. a bare CTA link
// with no query string. Kept in sync with kameleoonHelper.ts's SiteKeys hostnames.
const PORTAL_HOSTNAMES_BY_SOURCE_ID: Record<number, string[]> = {
  [BOAT_TRADER_SOURCE_ID]: [
    new URL(BOATTRADER_ORIGIN).hostname,
    new URL(BOATTRADER_QA_ORIGIN).hostname,
  ],
  [YACHTWORLD_SOURCE_ID]: ['www.yachtworld.com', 'qa.yachtworld.com'],
  [BOATSDOTCOM_SOURCE_ID]: ['www.boats.com', 'www.qa.boats.com'],
};

function resolveSourceIdFromHostname(hostname: string): number | undefined {
  const match = Object.entries(PORTAL_HOSTNAMES_BY_SOURCE_ID).find(([, hostnames]) =>
    hostnames.includes(hostname)
  );
  return match ? Number(match[0]) : undefined;
}

/**
 * Resolves a source id for portal branding purposes: prefers the explicit `source`
 * query param, falling back to hostname-based detection when it's absent so a bare
 * (no-`source`) CTA link into YachtWorld or Boats.com doesn't silently resolve to
 * Boat Trader branding/apply-path.
 */
export function resolvePortalSourceId(search: string, hostname: string): number | undefined {
  const fromParam = Number(new URLSearchParams(search).get('source')) || undefined;
  return fromParam ?? resolveSourceIdFromHostname(hostname);
}

/**
 * Every `applyPathPrefix` known across all portals, deduplicated. Lets callers
 * like `safeApplyHref.ts` recognize a URL that's already correctly prefixed for
 * some portal (even one other than the resolved source) and avoid double-prefixing it.
 */
export const KNOWN_APPLY_PATH_PREFIXES: readonly string[] = Array.from(
  new Set(
    Object.values(PORTAL_BRANDING_BY_SOURCE_ID).flatMap((byEnv) => [
      byEnv.qa.applyPathPrefix,
      byEnv.production.applyPathPrefix,
    ])
  )
);

/**
 * Resolves the portal-specific branding (labels/URLs) for a numeric loan/prequal
 * `source` id. Falls back to Boat Trader branding for an unrecognized, null, or
 * undefined source id, preserving the app's pre-existing BoatTrader-only default.
 */
export function getPortalBranding(sourceId?: number | null): PortalBranding {
  const byEnv =
    (sourceId != null && PORTAL_BRANDING_BY_SOURCE_ID[sourceId]) || BOAT_TRADER_BRANDING;
  return isProduction() ? byEnv.production : byEnv.qa;
}

// Only YachtWorld deviates from the default "boat" vessel copy today; add a
// portal's `label` here to override its copy (e.g. if Boats.com needs its own).
const VESSEL_COPY_BY_PORTAL_LABEL: Record<string, { noun: string; loanTypeLabel: string }> = {
  YachtWorld: { noun: 'yacht', loanTypeLabel: 'Yacht Loans' },
};

const DEFAULT_VESSEL_COPY = { noun: 'boat', loanTypeLabel: 'Boat Loans' };

/**
 * Resolves the singular vessel noun (e.g. "boat"/"yacht") for a portal's branding
 * `label`, defaulting to "boat" for portals without an override.
 */
export function getVesselNoun(label: string): string {
  return (VESSEL_COPY_BY_PORTAL_LABEL[label] ?? DEFAULT_VESSEL_COPY).noun;
}

/**
 * Resolves the loan-type copy (e.g. "Boat Loans"/"Yacht Loans") for a portal's
 * branding `label`, defaulting to "Boat Loans" for portals without an override.
 */
export function getLoanTypeLabel(label: string): string {
  return (VESSEL_COPY_BY_PORTAL_LABEL[label] ?? DEFAULT_VESSEL_COPY).loanTypeLabel;
}

export function isBoatTraderSource(sourceId?: number | null): boolean {
  const resolved =
    sourceId ?? resolvePortalSourceId(window.location.search, window.location.hostname);
  return resolved === BOAT_TRADER_SOURCE_ID;
}
